/**
 * enginePreProcess_Merge_TemplateTraversal.ts - Template traversal methods for EnginePreProcess
 * Port of C# EnginePreProcess_Merge_TemplateTraversal.cs
 * Contains processTemplateContent, resolveLatePlaceholder, processSlottedTemplate, processSlotContent
 * and processPlaceholder
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { TemplateOperation, TemplateOperationKind, PlaceholderIndex } from '../../model/index.js';
import { PreprocessedTemplate } from '../model/index.js';
import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import type { EnginePreProcess } from './enginePreProcess.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { SlottedTemplate } from '../model/slottedTemplate.js';
import type { SlotPlaceholder } from '../model/slotPlaceholder.js';
import type { TemplatePlaceholder } from '../model/templatePlaceholder.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';

/**
 * Walks a preprocessed template and resolves its placeholders and slots into final content.
 *
 * `latePlaceholders` carries the placeholder names that the DynamicView handler that produced
 * `content` introduced. They are resolved at the tail of this method, after the pre-parsed
 * slots/placeholders are merged — replacing the historical engine-side text scan with a
 * metadata-driven list supplied by the loader.
 */
export function processTemplateContent(this: EnginePreProcess, content: string, template: PreprocessedTemplate, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, latePlaceholders: string[] | null = null, clientMode: boolean = false): string {
    return timing.measure(TimingPhase.Render, () => {
    if (recursionDepth > this._loader!.maxRecursionDepth) {
        const errorMsg = `[ERROR] Maximum recursion depth (${this._loader!.maxRecursionDepth}) exceeded for template ${templateKey}. Possible circular template reference.`;
        Logger.error(errorMsg, 'EnginePreProcess');
        return errorMsg;
    }

    if (!content) {
        return content;
    }

    Logger.flow(`ProcessTemplateContent: enter templateKey=${templateKey} depth=${recursionDepth} slots=${template.slottedTemplates.length} placeholders=${template.placeholders.length} late=${latePlaceholders?.length ?? 0}`, "PreProcess.TemplateRecursion");
    let result = content;

    for (const slottedTemplate of template.slottedTemplates || []) {
        const slotIndex = result.indexOf(slottedTemplate.fullMatch);
        if (slotIndex < 0) {
            continue;
        }

        Logger.flow(`ProcessTemplateContent: slot=${slottedTemplate.name}`, "PreProcess.TemplateRecursion");
        const processedSlot = this._processSlottedTemplate(
            slottedTemplate,
            appSite,
            currentTemplateName,
            appView,
            templateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            timing,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = timing.measure(TimingPhase.Assemble, () => CommonUtil.replaceAllFrom(result, slotIndex, slottedTemplate.fullMatch, processedSlot));
    }

    for (const placeholder of template.placeholders || []) {
        const placeholderIndex = result.indexOf(placeholder.fullMatch);
        if (placeholderIndex < 0) {
            continue;
        }

        Logger.flow(`ProcessTemplateContent: placeholder=${placeholder.name}`, "PreProcess.TemplateRecursion");
        const processedPlaceholder = this._processPlaceholder(
            placeholder,
            appSite,
            currentTemplateName,
            appView,
            templateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            timing,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = timing.measure(TimingPhase.Assemble, () => CommonUtil.replaceAllFrom(result, placeholderIndex, placeholder.fullMatch, processedPlaceholder));
    }

    // Resolve any late-bound placeholders surfaced by the DynamicView handler that produced
    // `content`. The loader returns these names in order of first appearance — we honour that
    // order and advance a searchPos past each replacement to mirror the prior text-scan semantics.
    if (latePlaceholders && latePlaceholders.length > 0) {
        let searchPos = 0;
        for (const placeholderName of latePlaceholders) {
            const r = this._resolveLatePlaceholder(
                result,
                searchPos,
                placeholderName,
                appSite,
                currentTemplateName,
                appView,
                templateKey,
                operations,
                recursionDepth,
                componentHtml,
                genComponentHtml,
                timing,
                contextJson,
                flowStack,
                configJson,
                clientMode
            );
            result = r.result;
            searchPos = r.searchPos;
        }
    }

    return result;
    });
}

/**
 * Resolves a single late-bound placeholder (one entry from the loader's post-handler scan)
 * by loading its referenced template, applying its handlers, merging JSON, and recursing.
 * Replaces the next occurrence of {{placeholderName}} in `content` starting from `searchPos`,
 * then returns the updated content and an advanced searchPos past the inserted replacement.
 */
export function resolveLatePlaceholder(this: EnginePreProcess, content: string, searchPos: number, placeholderName: string, appSite: string, currentTemplateName: string, appView: string | null, templateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): { result: string; searchPos: number } {
    const fullMatch = '{{' + placeholderName + '}}';
    const openStart = content.indexOf(fullMatch, searchPos);
    if (openStart < 0) {
        // Already replaced by an earlier iteration (duplicate name) or no longer present.
        return { result: content, searchPos };
    }
    const closeStart = openStart + fullMatch.length - 2;

    const nestedOperations: TemplateOperation[] = [];
    const newFlowStack = [...(flowStack || []), placeholderName];

    let { template: childTemplate, json: childJson, resolvedTemplateName, resolvedAppSite, viewHtml: wrappedHtml, latePlaceholders: replacementLatePlaceholders } =
        this._loader!.getTemplateWithView(appSite, currentTemplateName, placeholderName, timing, appView, newFlowStack, configJson, clientMode);
    if (wrappedHtml !== childTemplate.originalContent) {
        const wrappedTemplate = new PreprocessedTemplate();
        wrappedTemplate.originalContent = wrappedHtml;
        wrappedTemplate.actualTemplateName = childTemplate.actualTemplateName;
        wrappedTemplate.placeholders = childTemplate.placeholders;
        wrappedTemplate.slottedTemplates = childTemplate.slottedTemplates;
        wrappedTemplate.replacementMappings = childTemplate.replacementMappings;
        wrappedTemplate.jsonArrayPlaceholders = childTemplate.jsonArrayPlaceholders;
        childTemplate = wrappedTemplate;
    }

    if (resolvedTemplateName && resolvedTemplateName !== placeholderName && newFlowStack.length > 0) {
        newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
    }

    let replacement: string;
    if (childTemplate == null) {
        replacement = this._loader!.defaultReplaceHtml;
        resolvedTemplateName = this._loader!.defaultReplaceTemplateName;
        resolvedAppSite = appSite;
    } else {
        replacement = childTemplate.originalContent || '';
    }

    const replacedWith = `${resolvedAppSite}_${resolvedTemplateName || placeholderName}`;
    const isPlaceholderSkipMarked = placeholderName.startsWith('!');
    const isSkippedOrMissing = resolvedTemplateName === this._loader!.defaultSkipTemplateName || resolvedTemplateName === this._loader!.defaultReplaceTemplateName;

    if (childTemplate && childTemplate.originalContent && replacement && !isPlaceholderSkipMarked && !isSkippedOrMissing) {
        const jsonToMerge = childJson || new JsonObject();
        const placeholderIndex = PlaceholderIndex.fromParsed(childTemplate.jsonArrayPlaceholders, childTemplate.jsonKeyPlaceholders);
        replacement = timing.measure(TimingPhase.Bind, () => JsonMergeUtil.mergeTemplateWithJson(
            replacement,
            jsonToMerge,
            replacedWith,
            resolvedTemplateName || placeholderName,
            nestedOperations,
            this._loader!.maxArrayOperationItems,
            this._loader!.defaultReplaceHtml,
            this._loader!.defaultSkipJson,
            placeholderIndex,
            contextJson,
            configJson
        ));

        if (genComponentHtml) {
            componentHtml.set(placeholderName, JsonValue.string(''));
        }

        replacement = this._processTemplateContent(
            replacement,
            childTemplate,
            appSite,
            placeholderName,
            appView,
            replacedWith,
            nestedOperations,
            recursionDepth + 1,
            componentHtml,
            genComponentHtml,
            timing,
            contextJson,
            newFlowStack,
            configJson,
            replacementLatePlaceholders,
            clientMode
        );

        if (genComponentHtml) {
            componentHtml.set(placeholderName, JsonValue.string(replacement));
        }
    }

    let opType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
    if (resolvedTemplateName === this._loader!.defaultSkipTemplateName) {
        opType = TemplateOperationKind.ComponentSkip;
    } else if (resolvedTemplateName === this._loader!.defaultReplaceTemplateName) {
        opType = TemplateOperationKind.ComponentMissing;
    }

    operations.push(new TemplateOperation({
        templateName: templateKey,
        kind: opType,
        target: fullMatch,
        replacedWith: replacedWith,
        operations: nestedOperations
    }));

    const result = timing.measure(TimingPhase.Assemble, () => content.substring(0, openStart) + replacement + content.substring(closeStart + 2));
    return { result, searchPos: openStart + replacement.length };
}

export function processSlottedTemplate(this: EnginePreProcess, slottedTemplate: SlottedTemplate, appSite: string, currentTemplateName: string, appView: string | null, parentTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): string {
    const nestedOperations: TemplateOperation[] = [];

    const cleanTemplateName = slottedTemplate.name.replace(/^!/, '');

    const newFlowStack = [...(flowStack || []), cleanTemplateName];

    // Resolve the slot's template with DynamicView handlers applied at the loader (WS-A).
    // The parsed structure is kept; the view-wrapped HTML becomes the child content.
    let { template: childTemplate, json: childJson, resolvedTemplateName, resolvedAppSite, viewHtml: slotWrappedHtml, latePlaceholders: slotLatePlaceholders } =
        this._loader!.getTemplateWithView(appSite, currentTemplateName, slottedTemplate.name, timing, appView, newFlowStack, configJson, clientMode);
    if (slotWrappedHtml !== childTemplate.originalContent) {
        const wrappedSlotTemplate = new PreprocessedTemplate();
        wrappedSlotTemplate.originalContent = slotWrappedHtml;
        wrappedSlotTemplate.actualTemplateName = childTemplate.actualTemplateName;
        wrappedSlotTemplate.placeholders = childTemplate.placeholders;
        wrappedSlotTemplate.slottedTemplates = childTemplate.slottedTemplates;
        wrappedSlotTemplate.replacementMappings = childTemplate.replacementMappings;
        wrappedSlotTemplate.jsonArrayPlaceholders = childTemplate.jsonArrayPlaceholders;
        childTemplate = wrappedSlotTemplate;
    }

    if (resolvedTemplateName !== cleanTemplateName && newFlowStack.length > 0) {
        newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
    }

    const replacedWith = `${resolvedAppSite}_${resolvedTemplateName}`;
    Logger.flow(`ProcessSlottedTemplate: slot=${slottedTemplate.name} resolved=${resolvedTemplateName} slotCount=${slottedTemplate.slots ? slottedTemplate.slots.length : 0} childSlots=${childTemplate.slottedTemplates.length} childPlaceholders=${childTemplate.placeholders.length}`, "PreProcess.SlotExpansion");

    const isSlotSkipMarked = slottedTemplate.name.startsWith('!');
    const isSlotSkippedOrMissing = resolvedTemplateName === this._loader!.defaultSkipTemplateName || resolvedTemplateName === this._loader!.defaultReplaceTemplateName;

    if (genComponentHtml) {
        componentHtml.set(slottedTemplate.name, JsonValue.string(''));
    }

    let childContent = childTemplate.originalContent;

    if (!childContent) {
        const opType = resolvedTemplateName === this._loader!.defaultSkipTemplateName ? TemplateOperationKind.SlotSkip : TemplateOperationKind.SlotMissing;
        const cleanName = slottedTemplate.name.replace(/^!/, '');
        const targetMarker = slottedTemplate.name.startsWith('!')
            ? `{{!#${cleanName}}}`
            : `{{#${cleanName}}}`;
        const operation = new TemplateOperation();
        operation.templateName = parentTemplateKey;
        operation.kind = opType;
        operation.target = targetMarker;
        operation.replacedWith = replacedWith;
        operation.operations = nestedOperations;
        operations.push(operation);
        return childContent || '';
    }

    if (!isSlotSkipMarked && !isSlotSkippedOrMissing) {
        const jsonToMerge = childJson || new JsonObject();
        const defaultHtmlForMerge = resolvedTemplateName === this._loader!.defaultSkipTemplateName
            ? this._loader!.defaultSkipHtml
            : this._loader!.defaultReplaceHtml;

        const slotPlaceholderIndex = PlaceholderIndex.fromParsed(childTemplate.jsonArrayPlaceholders, childTemplate.jsonKeyPlaceholders);
        childContent = timing.measure(TimingPhase.Bind, () => JsonMergeUtil.mergeTemplateWithJson(
            childContent,
            jsonToMerge,
            replacedWith,
            resolvedTemplateName || slottedTemplate.name,
            nestedOperations,
            this._loader!.maxArrayOperationItems,
            defaultHtmlForMerge,
            this._loader!.defaultSkipJson,
            slotPlaceholderIndex,
            contextJson,
            configJson
        ));

        for (const slot of slottedTemplate.slots || []) {
            if (!childContent.includes(slot.slotKey)) {
                continue;
            }

            Logger.flow(`ProcessSlottedTemplate: slotKey=${slot.slotKey} nestedSlots=${slot.nestedSlottedTemplates ? slot.nestedSlottedTemplates.length : 0} nestedPlaceholders=${slot.nestedPlaceholders ? slot.nestedPlaceholders.length : 0}`, "PreProcess.SlotExpansion");
            const slotContentOperations: TemplateOperation[] = [];
            const processedSlotContent = this._processSlotContent(
                slot,
                appSite,
                currentTemplateName,
                appView,
                replacedWith,
                slotContentOperations,
                recursionDepth + 1,
                componentHtml,
                genComponentHtml,
                timing,
                contextJson,
                newFlowStack,
                configJson,
                clientMode
            );

            childContent = timing.measure(TimingPhase.Assemble, () => childContent.replace(slot.slotKey, processedSlotContent));

            const slotName = slot.slotKey.replace('{{$', '').replace('}}', '');
            const slotOperation = new TemplateOperation();
            slotOperation.templateName = replacedWith;
            slotOperation.kind = TemplateOperationKind.SlotMerge;
            slotOperation.target = slot.slotKey;
            slotOperation.replacedWith = slotName;
            slotOperation.operations = slotContentOperations;
            nestedOperations.push(slotOperation);
        }

        childContent = CommonUtil.removeRemainingSlotPlaceholders(childContent);

        childContent = this._processTemplateContent(
            childContent,
            childTemplate,
            appSite,
            cleanTemplateName,
            appView,
            replacedWith,
            nestedOperations,
            recursionDepth + 1,
            componentHtml,
            genComponentHtml,
            timing,
            contextJson,
            newFlowStack,
            configJson,
            slotLatePlaceholders,
            clientMode
        );
    }

    if (genComponentHtml) {
        componentHtml.set(slottedTemplate.name, JsonValue.string(childContent));
    }

    let finalOpType: TemplateOperationKind = TemplateOperationKind.SlotReplace;
    if (resolvedTemplateName === this._loader!.defaultSkipTemplateName) {
        finalOpType = TemplateOperationKind.SlotSkip;
    } else if (resolvedTemplateName === this._loader!.defaultReplaceTemplateName) {
        finalOpType = TemplateOperationKind.SlotMissing;
    }

    const cleanNameFinal = slottedTemplate.name.replace(/^!/, '');
    const targetMarkerFinal = slottedTemplate.name.startsWith('!')
        ? `{{!#${cleanNameFinal}}}`
        : `{{#${cleanNameFinal}}}`;
    const operation = new TemplateOperation();
    operation.templateName = parentTemplateKey;
    operation.kind = finalOpType;
    operation.target = targetMarkerFinal;
    operation.replacedWith = replacedWith;
    operation.operations = nestedOperations;
    operations.push(operation);

    return childContent;
}

export function processSlotContent(this: EnginePreProcess, slot: SlotPlaceholder, appSite: string, currentTemplateName: string, appView: string | null, contextTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): string {
    let result = slot.content;

    for (const nestedSlot of slot.nestedSlottedTemplates || []) {
        if (!result.includes(nestedSlot.fullMatch)) {
            continue;
        }

        Logger.flow(`ProcessSlotContent: nestedSlot=${nestedSlot.name}`, "PreProcess.SlotExpansion");
        const processedNestedSlot = this._processSlottedTemplate(
            nestedSlot,
            appSite,
            currentTemplateName,
            appView,
            contextTemplateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            timing,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = timing.measure(TimingPhase.Assemble, () => result.replaceAll(nestedSlot.fullMatch, processedNestedSlot));
    }

    for (const nestedPlaceholder of slot.nestedPlaceholders || []) {
        if (!result.includes(nestedPlaceholder.fullMatch)) {
            continue;
        }

        Logger.flow(`ProcessSlotContent: nestedPlaceholder=${nestedPlaceholder.name}`, "PreProcess.SlotExpansion");
        Logger.flow(`ProcessTemplateContent: placeholder=${nestedPlaceholder.name}`, "PreProcess.TemplateRecursion");
        const processedNested = this._processPlaceholder(
            nestedPlaceholder,
            appSite,
            currentTemplateName,
            appView,
            contextTemplateKey,
            operations,
            recursionDepth,
            componentHtml,
            genComponentHtml,
            timing,
            contextJson,
            flowStack,
            configJson,
            clientMode
        );

        result = timing.measure(TimingPhase.Assemble, () => result.replaceAll(nestedPlaceholder.fullMatch, processedNested));
    }

    return result;
}

export function processPlaceholder(this: EnginePreProcess, placeholder: TemplatePlaceholder, appSite: string, currentTemplateName: string, appView: string | null, parentTemplateKey: string, operations: TemplateOperation[], recursionDepth: number, componentHtml: JsonObject, genComponentHtml: boolean, timing: PhaseTiming, contextJson: JsonObject | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): string {
    const nestedOperations: TemplateOperation[] = [];

    const cleanPlaceholderName = placeholder.name.replace(/^!/, '');

    const newFlowStack = [...(flowStack || []), cleanPlaceholderName];

    // Resolve the placeholder's template with DynamicView handlers applied at the loader (WS-A).
    // The parsed structure is kept; the view-wrapped HTML becomes the child content.
    let { template: childTemplate, json: childJson, resolvedTemplateName, resolvedAppSite, viewHtml: placeholderWrappedHtml, latePlaceholders: placeholderLatePlaceholders } =
        this._loader!.getTemplateWithView(appSite, currentTemplateName, placeholder.name, timing, appView, newFlowStack, configJson, clientMode);
    if (placeholderWrappedHtml !== childTemplate.originalContent) {
        const wrappedPlTemplate = new PreprocessedTemplate();
        wrappedPlTemplate.originalContent = placeholderWrappedHtml;
        wrappedPlTemplate.actualTemplateName = childTemplate.actualTemplateName;
        wrappedPlTemplate.placeholders = childTemplate.placeholders;
        wrappedPlTemplate.slottedTemplates = childTemplate.slottedTemplates;
        wrappedPlTemplate.replacementMappings = childTemplate.replacementMappings;
        wrappedPlTemplate.jsonArrayPlaceholders = childTemplate.jsonArrayPlaceholders;
        childTemplate = wrappedPlTemplate;
    }

    if (resolvedTemplateName !== cleanPlaceholderName && newFlowStack.length > 0) {
        newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
    }

    const replacedWith = `${resolvedAppSite}_${resolvedTemplateName}`;

    let childContent = childTemplate.originalContent;

    const isPlaceholderSkipMarked = placeholder.name.startsWith('!');
    const isSkippedOrMissing = resolvedTemplateName === this._loader!.defaultSkipTemplateName || resolvedTemplateName === this._loader!.defaultReplaceTemplateName;

    if (genComponentHtml) {
        componentHtml.set(placeholder.name, JsonValue.string(''));
    }

    if (!childContent) {
        const opType = resolvedTemplateName === this._loader!.defaultSkipTemplateName ? TemplateOperationKind.ComponentSkip : TemplateOperationKind.ComponentMissing;
        const operation = new TemplateOperation();
        operation.templateName = parentTemplateKey;
        operation.kind = opType;
        operation.target = placeholder.fullMatch;
        operation.replacedWith = replacedWith;
        operation.operations = nestedOperations;
        operations.push(operation);
        return childContent || '';
    }

    if (!isPlaceholderSkipMarked && !isSkippedOrMissing) {
        const defaultHtmlForMerge = resolvedTemplateName === this._loader!.defaultSkipTemplateName
            ? this._loader!.defaultSkipHtml
            : this._loader!.defaultReplaceHtml;

        const childPlaceholderIndex = PlaceholderIndex.fromParsed(childTemplate.jsonArrayPlaceholders, childTemplate.jsonKeyPlaceholders);
        const jsonToMerge = childJson || new JsonObject();
        childContent = timing.measure(TimingPhase.Bind, () => JsonMergeUtil.mergeTemplateWithJson(
            childContent,
            jsonToMerge,
            replacedWith,
            resolvedTemplateName || placeholder.name,
            nestedOperations,
            this._loader!.maxArrayOperationItems,
            defaultHtmlForMerge,
            this._loader!.defaultSkipJson,
            childPlaceholderIndex,
            contextJson,
            configJson
        ));

        childContent = this._processTemplateContent(
            childContent,
            childTemplate,
            appSite,
            cleanPlaceholderName,
            appView,
            replacedWith,
            nestedOperations,
            recursionDepth + 1,
            componentHtml,
            genComponentHtml,
            timing,
            contextJson,
            newFlowStack,
            configJson,
            placeholderLatePlaceholders,
            clientMode
        );
    }

    if (genComponentHtml) {
        componentHtml.set(placeholder.name, JsonValue.string(childContent));
    }

    let finalOpType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
    if (resolvedTemplateName === this._loader!.defaultSkipTemplateName) {
        finalOpType = TemplateOperationKind.ComponentSkip;
    } else if (resolvedTemplateName === this._loader!.defaultReplaceTemplateName) {
        finalOpType = TemplateOperationKind.ComponentMissing;
    }

    const operation = new TemplateOperation();
    operation.templateName = parentTemplateKey;
    operation.kind = finalOpType;
    operation.target = placeholder.fullMatch;
    operation.replacedWith = replacedWith;
    operation.operations = nestedOperations;
    operations.push(operation);

    return childContent;
}
